import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../lib/logger';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface Props {
    children: ReactNode;
    /** Custom fallback UI to show when an error occurs */
    fallback?: ReactNode;
    /** Name of the feature/section for better error context */
    featureName?: string;
    /** Whether to show retry button (default: true) */
    showRetry?: boolean;
    /** Callback when retry is clicked */
    onRetry?: () => void;
    /** Whether to show home button (default: false for feature boundaries) */
    showHome?: boolean;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorId: string | null;
}

/**
 * Error boundary component that catches JavaScript errors anywhere in child component tree
 * and displays a fallback UI instead of crashing the whole app.
 *
 * Usage:
 * - App-level: Wraps entire app to catch all unhandled errors
 * - Feature-level: Wraps individual features (Dashboard, Documents, etc.) for granular error handling
 *
 * @example
 * // Feature-level boundary
 * <ErrorBoundary featureName="Dashboard" onRetry={() => queryClient.invalidateQueries()}>
 *   <Dashboard />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false, error: null, errorId: null };

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Generate a unique error ID for support reference
        const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
        return { hasError: true, error, errorId };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const { featureName } = this.props;
        logger.error('ErrorBoundary caught error', error, {
            componentStack: errorInfo.componentStack,
            featureName: featureName || 'Unknown',
            errorId: this.state.errorId
        });
    }

    handleRetry = () => {
        const { onRetry } = this.props;
        // Reset error state first
        this.setState({ hasError: false, error: null, errorId: null });
        // Call custom retry handler if provided
        if (onRetry) {
            onRetry();
        }
    };

    handleGoHome = () => {
        window.location.href = '/dashboard';
    };

    render() {
        const { hasError, error, errorId } = this.state;
        const { children, fallback, featureName, showRetry = true, showHome = false } = this.props;

        if (hasError) {
            // Use custom fallback if provided
            if (fallback) {
                return fallback;
            }

            // Default error UI
            return (
                <div className="flex items-center justify-center min-h-[300px] p-4">
                    <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-center mb-4">
                            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" aria-hidden="true" />
                            </div>
                        </div>

                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            {featureName ? `${featureName} Error` : 'Something went wrong'}
                        </h2>

                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {featureName
                                ? `We encountered an issue loading ${featureName.toLowerCase()}. This section may be temporarily unavailable.`
                                : 'We apologize for the inconvenience. Please try again.'}
                        </p>

                        {/* Error details for debugging (development only) */}
                        {import.meta.env.DEV && error && (
                            <details className="mb-4 text-left">
                                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                                    Technical details
                                </summary>
                                <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-auto max-h-32">
                                    {error.message}
                                </pre>
                            </details>
                        )}

                        {/* Error reference ID */}
                        {errorId && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                                Reference: {errorId}
                            </p>
                        )}

                        <div className="flex gap-3 justify-center">
                            {showRetry && (
                                <button
                                    onClick={this.handleRetry}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-storgia-blurple text-white rounded-lg hover:bg-storgia-blurple/90 transition-colors focus:outline-none focus:ring-2 focus:ring-storgia-blurple focus:ring-offset-2"
                                >
                                    <RefreshCw className="w-4 h-4" aria-hidden="true" />
                                    Try Again
                                </button>
                            )}
                            {showHome && (
                                <button
                                    onClick={this.handleGoHome}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                                >
                                    <Home className="w-4 h-4" aria-hidden="true" />
                                    Go to Dashboard
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        return children;
    }
}

/**
 * Higher-order component to wrap a component with an error boundary
 * Useful for wrapping lazy-loaded route components
 *
 * @example
 * const SafeDashboard = withErrorBoundary(Dashboard, { featureName: 'Dashboard' });
 */
export function withErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    boundaryProps: Omit<Props, 'children'> = {}
): React.FC<P> {
    const WithErrorBoundary: React.FC<P> = (props) => (
        <ErrorBoundary {...boundaryProps}>
            <WrappedComponent {...props} />
        </ErrorBoundary>
    );

    WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return WithErrorBoundary;
}
